import { zodResolver } from '@hookform/resolvers/zod';
import { Anchor, Button, Container, Paper, PasswordInput, Text, Title } from '@mantine/core';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  ChangePasswordSchema,
  PASSWORD_MIN_LENGTH,
  changePasswordDefaultValues,
  changePasswordSchema,
} from '../model/ChangePasswordSchema';

export interface ChangePasswordProps {
  onSubmit: (values: ChangePasswordSchema) => void;
  onLogout: () => void;
  isSubmitting: boolean;
}

export function ChangePassword({ onSubmit, onLogout, isSubmitting }: ChangePasswordProps) {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: changePasswordDefaultValues,
    resolver: zodResolver(changePasswordSchema),
  });

  return (
    <Container
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      size={420}
      my={0}
    >
      <Title ta="center">{t('auth.changePassword.title')}</Title>
      <Paper
        withBorder
        shadow="xs"
        miw="320px"
        p={30}
        mt={30}
        radius="md"
      >
        <Text
          size="sm"
          mb="md"
        >
          {t('auth.changePassword.description')}
        </Text>
        <PasswordInput
          {...register('currentPassword')}
          error={errors.currentPassword?.message}
          label={t('auth.changePassword.currentPassword')}
          disabled={isSubmitting}
          autoComplete="current-password"
          required
        />
        <PasswordInput
          {...register('newPassword')}
          error={errors.newPassword?.message}
          label={t('auth.changePassword.newPassword')}
          description={t('auth.changePassword.policy', { min: PASSWORD_MIN_LENGTH })}
          disabled={isSubmitting}
          autoComplete="new-password"
          required
          mt="md"
        />
        <PasswordInput
          {...register('confirmPassword')}
          error={errors.confirmPassword?.message}
          label={t('auth.changePassword.confirmPassword')}
          disabled={isSubmitting}
          autoComplete="new-password"
          required
          mt="md"
        />
        <Button
          fullWidth
          mt="xl"
          type="submit"
          loading={isSubmitting}
        >
          {t('auth.changePassword.submit')}
        </Button>
        <Text
          size="sm"
          mt="md"
          ta="center"
        >
          <Anchor
            component="button"
            type="button"
            onClick={onLogout}
            disabled={isSubmitting}
          >
            {t('profileMenu.logout')}
          </Anchor>
        </Text>
      </Paper>
    </Container>
  );
}
